import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { ReactNode } from 'react';

import { DragDropProvider } from '@dnd-kit/react';
import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { createContext, use, useMemo } from 'react';

import type { FsDragData } from '@/hooks/fs/useFsDragDrop';

import { FS_DRAG_TYPE, useFsDragDrop } from '@/hooks/fs/useFsDragDrop';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Context value for FS drag drop operations
 */
interface FsDragDropContextValue {
  isDragging: boolean;
  draggedItem: FsDragData | null;
  isPending: boolean;
}

const FsDragDropContext = createContext<FsDragDropContextValue | null>(null);

/**
 * Hook to access the FS drag drop context
 */
export function useFsDragDropContext(): FsDragDropContextValue {
  const context = use(FsDragDropContext);
  if (!context) {
    throw new Error('useFsDragDropContext must be used within FsDragDropProvider');
  }
  return context;
}

interface FsDragDropProviderProps {
  children: ReactNode;
  disabled?: boolean;
  directoryId: string;
}

/**
 * Provider component that enables drag and drop for file system items.
 * Wraps children with DragDropProvider from @dnd-kit/react and provides
 * context for drag state and operations.
 */
export function FsDragDropProvider(fsDragDropProviderProps: FsDragDropProviderProps) {
  const { children, disabled = false, directoryId } = fsDragDropProviderProps;
  const { isDragging, draggedItem, handleDragStart, handleDragEnd, isPending } = useFsDragDrop(directoryId);

  const contextValue = useMemo<FsDragDropContextValue>(
    () => ({
      isDragging,
      draggedItem,
      isPending,
    }),
    [isDragging, draggedItem, isPending]
  );

  if (disabled) {
    return <FsDragDropContext value={contextValue}>{children}</FsDragDropContext>;
  }

  return (
    <FsDragDropContext value={contextValue}>
      <DragDropProvider
        onDragStart={(event) => {
          const data = event.operation.source?.data as FsDragData | undefined;
          if (data) {
            handleDragStart(data);
          }
        }}
        onDragEnd={async (event) => {
          const { canceled, operation } = event;
          const targetData = operation.target?.data as { directory: DirectoryPublic } | undefined;

          let targetDirectoryId: string | null = null;
          if (targetData?.directory) {
            targetDirectoryId = targetData.directory.id;
          }

          try {
            await handleDragEnd(targetDirectoryId, canceled);
          } catch (error) {
            toasterMessageHandler(fsErrorMessages, error as Error | null);
          }
        }}
      >
        {children}
      </DragDropProvider>
    </FsDragDropContext>
  );
}

/**
 * Helper to create drag data for a file
 */
export function createFileDragData(file: FilePublicWithPermissions): FsDragData {
  return {
    type: FS_DRAG_TYPE.FILE,
    entity: file,
  };
}

/**
 * Helper to create drag data for a directory
 */
export function createDirectoryDragData(directory: DirectoryPublic): FsDragData {
  return {
    type: FS_DRAG_TYPE.DIRECTORY,
    entity: directory,
  };
}
